import React, {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { AuthContext } from "../../AuthProvider";
import { LoadingPopup } from "../LoadingPopup/LoadingPopup";
import sendLogError from "../../sendLogError";
import { mockUserData } from "../../mocks/fakeAuth";
import { authenticatedFetch } from "../../utils/authenticatedFetch";
import { IElectionDates, IMyMontepioContext, IMyMontepioUser } from "./types";

type TContextProviderProps = {
  children: ReactNode;
};

type TContextProviderState = {
  mymUsers: IMyMontepioUser[];
  selectedUser: IMyMontepioUser;
  selectedUserID: string;
  tokenUser: string;
  userName: string;
  parentUser: IMyMontepioUser;
  idToken: string;
  msalInstance: IMyMontepioContext["msalInstance"] | null;
  isLoggedIn: boolean;
  isMobileAuth: boolean;
  loading: boolean;
  isElectionsTime: boolean;
  electionDates: IElectionDates | null;
};

type TAuthContext = Pick<
  IMyMontepioContext,
  "idToken" | "isLoggedIn" | "msalInstance" | "isMobileAuth" | "performLoginRedirect"
> & {
  logout?: () => void | Promise<void>;
};

type TAssociatesResponse = {
  value?: string;
};

type TContextProviderStateUpdate =
  | Partial<TContextProviderState>
  | ((currentState: TContextProviderState) => Partial<TContextProviderState>);

const initialState: TContextProviderState = {
  mymUsers: [],
  selectedUser: {},
  selectedUserID: "",
  tokenUser: "",
  userName: "",
  parentUser: {},
  idToken: "",
  msalInstance: null,
  isLoggedIn: false,
  isMobileAuth: false,
  loading: true,
  isElectionsTime: false,
  electionDates: null,
};

const ContextConstructor = React.createContext<IMyMontepioContext | undefined>(undefined);

const getErrorMessage = (error: unknown) =>
  error instanceof Error ? error.message : "Erro desconhecido";

export default ContextConstructor;

export const ContextProvider = ({ children }: TContextProviderProps) => {
  const authContext = useContext(AuthContext) as TAuthContext;
  const [state, setState] = useState<TContextProviderState>(initialState);
  const hasLoadedAuthenticatedDataRef = useRef(false);

  const updateState = useCallback((update: TContextProviderStateUpdate) => {
    setState((currentState) => ({
      ...currentState,
      ...(typeof update === "function" ? update(currentState) : update),
    }));
  }, []);

  const currentIdToken = state.idToken;

  const contextInit = useCallback<IMyMontepioContext["contextInit"]>(
    (usersProfile, selectedUserIDClientID, isActualUser, selectedUserProfile) => {
      updateState({
        mymUsers: usersProfile,
        selectedUser: selectedUserProfile,
        selectedUserID: selectedUserIDClientID,
        tokenUser: isActualUser,
        parentUser: selectedUserProfile,
        loading: false,
      });
    },
    [updateState],
  );

  const fetchElectionDates = useCallback(async () => {
    try {
      if (!currentIdToken) return;

      const response = await authenticatedFetch("/Elections/GetElectionDates");

      if (!response.ok) {
        throw new Error(`Erro HTTP: ${response.status}`);
      }

      const data = (await response.json()) as IElectionDates;

      if (!data.actualDate || !data.endDate) {
        throw new Error("Dados inválidos na resposta");
      }

      const currentDate = new Date(data.actualDate);
      const endDate = new Date(data.endDate);

      updateState({
        isElectionsTime: currentDate < endDate,
        electionDates: data,
      });
    } catch (error: unknown) {
      console.error("Error fetching election dates:", error);

      updateState({
        isElectionsTime: false,
        electionDates: null,
      });

      sendLogError(getErrorMessage(error), "fetchElectionDates");
    }
  }, [currentIdToken, updateState]);

  const handleLogout = useCallback(async () => {
    const { msalInstance, idToken } = state;

    if (authContext.isMobileAuth) {
      await authContext.logout?.();
      return;
    }

    if (!msalInstance) return;

    try {
      await msalInstance.logoutRedirect({
        postLogoutRedirectUri: "https://www.montepio.org",
        idTokenHint: idToken,
      });
    } catch (error: unknown) {
      console.error("Error during logout:", error);
    }
  }, [authContext, state]);

  const changeselectedUserID = useCallback<IMyMontepioContext["changeselectedUserID"]>(
    (associate) => {
      updateState({
        selectedUserID: associate.clientID ?? "",
        selectedUser: associate,
      });
    },
    [updateState],
  );

  const populateAssociateData = useCallback(async () => {
    try {
      if (!currentIdToken) return;

      const resAssociates = await authenticatedFetch("/Profile/GetMymAssociates");

      if (!resAssociates.ok) {
        throw new Error(`Erro ao obter associados: ${resAssociates.status}`);
      }

      const data = (await resAssociates.json()) as TAssociatesResponse;
      let parsed: unknown = [];

      try {
        parsed = data.value ? JSON.parse(data.value) : [];
      } catch (error: unknown) {
        console.error("Erro a fazer parse de associados", error);
      }

      const associates: IMyMontepioUser[] = Array.isArray(parsed) ? parsed : [];
      const resProfile = await authenticatedFetch("/Profile/GetProfile");

      if (!resProfile.ok) {
        throw new Error(`Erro ao obter perfil: ${resProfile.status}`);
      }

      const userProfileResponse = ((await resProfile.json()) ?? {}) as IMyMontepioUser;
      const isValidProfile =
        Object.keys(userProfileResponse).length > 0 && Boolean(userProfileResponse.clientID);

      let selectedUserClientID = "";
      let finalAssociates = associates;

      if (isValidProfile) {
        finalAssociates = [...associates, userProfileResponse].map((associate) => {
          const isTokenUser = associate.clientID === userProfileResponse.clientID;

          if (isTokenUser) {
            selectedUserClientID = associate.clientID ?? "";
          }

          return {
            ...associate,
            isTokenUser,
          };
        });
      }

      contextInit(
        finalAssociates,
        selectedUserClientID,
        userProfileResponse.clientID ?? "",
        userProfileResponse,
      );

      updateState({ loading: false });
    } catch (error: unknown) {
      console.error("Error in populateAssociateData:", error);

      updateState({ loading: false });
      sendLogError(getErrorMessage(error), "populateAssociateData");
    }
  }, [contextInit, currentIdToken, updateState]);

  useEffect(() => {
    updateState({
      idToken: authContext.idToken,
      isLoggedIn: authContext.isLoggedIn,
      msalInstance: authContext.msalInstance,
      isMobileAuth: authContext.isMobileAuth,
    });
  }, [
    authContext.idToken,
    authContext.isLoggedIn,
    authContext.isMobileAuth,
    authContext.msalInstance,
    updateState,
  ]);

  useEffect(() => {
    if (!state.idToken) {
      hasLoadedAuthenticatedDataRef.current = false;
      return;
    }

    if (hasLoadedAuthenticatedDataRef.current) return;
    hasLoadedAuthenticatedDataRef.current = true;

    const isFakeAuth = process.env.REACT_APP_FAKE_AUTH === "true" && !state.isMobileAuth;

    if (isFakeAuth) {
      mockUserData(updateState);
    } else {
      populateAssociateData();
    }

    fetchElectionDates();
  }, [fetchElectionDates, populateAssociateData, state.idToken, state.isMobileAuth, updateState]);

  const contextValue = useMemo<IMyMontepioContext>(
    () => ({
      mymUsers: state.mymUsers,
      selectedUser: state.selectedUser,
      selectedUserID: state.selectedUserID,
      tokenUser: state.tokenUser,
      contextInit,
      changeselectedUserID,
      parentUser: state.parentUser,
      idToken: state.idToken,
      isLoggedIn: state.isLoggedIn,
      msalInstance: state.msalInstance,
      isMobileAuth: state.isMobileAuth,
      handleLogout,
      isElectionsTime: state.isElectionsTime,
      electionDates: state.electionDates,
      performLoginRedirect: authContext.performLoginRedirect,
    }),
    [authContext.performLoginRedirect, changeselectedUserID, contextInit, handleLogout, state],
  );

  if (state.loading) {
    return <LoadingPopup />;
  }

  return <ContextConstructor.Provider value={contextValue}>{children}</ContextConstructor.Provider>;
};
